/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2024, Zumper
 */
/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { BREAKPOINT_MOBILE } from '@zcode-ui/themes'
import { createLazyLoadableSection } from '@zcode/lazy-loadable-section'
import { ErrorFallback, LoadingFallback } from '@zcode/nr/es/shared/components/Fallback'
import { logger } from '@zcode/utils'
import type { ErrorBoundaryProps } from 'react-error-boundary'

const fallbackStyle = css`
  height: 500px;
  @media (max-width: ${BREAKPOINT_MOBILE}) {
    height: 650px;
  }
`

const onRenderError: ErrorBoundaryProps['onError'] = (error, info) => {
  logger.error('render error for NationalRentReport lazy loadable section', info, error)
}

const onLoadingError = (error: unknown) => {
  logger.error('loading error for NationalRentReport lazy loadable section', error)
}

const loadFn = /* #__LOADABLE__ */ async () => await import('./NationalRentReport')

const handleReload = async () => await NationalRentReportLazyLoadableSection.reload()

const options = {
  errorFallback: () => (
    <ErrorFallback
      css={fallbackStyle}
      message="Error loading Zumper National Rent Report"
      onClickReload={handleReload}
    />
  ),

  loadingFallback: () => <LoadingFallback css={fallbackStyle} name="Zumper National Rent Report" />,
  onRenderError,
  onLoadingError,
}

export const NationalRentReportLazyLoadableSection = createLazyLoadableSection(loadFn, options)
